/**
 * VoiceActionsContext — global registry of voice-callable actions.
 *
 * Every page that wants to be voice-controlled calls
 *   useVoiceAction({ phrases: ["delete my account", "remove account"],
 *                    handler: () => onDelete() });
 * The HustleVoice component matches every transcript against the
 * combined registry before falling back to LLM intent classification.
 *
 * This is the difference between "voice search" and "voice control" —
 * voice search just understands intent; voice control fires real
 * buttons. Every page registers its key actions here.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const VoiceActionsContext = createContext(null);

/* ── Helpers ───────────────────────────────────────────────────── */
function _norm(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "").trim();
}

function _score(transcript, phrases) {
  const t = _norm(transcript);
  if (!t) return 0;
  let best = 0;
  for (const p of phrases) {
    const pn = _norm(p);
    if (!pn) continue;
    if (t === pn) return 100;
    if (t.includes(pn)) best = Math.max(best, 80);
    // Word-overlap fallback
    const tw = new Set(t.split(/\s+/));
    const pw = pn.split(/\s+/);
    const hits = pw.filter((w) => tw.has(w)).length;
    if (pw.length) {
      best = Math.max(best, Math.round((hits / pw.length) * 70));
    }
  }
  return best;
}

/* ── Provider ──────────────────────────────────────────────────── */
export function VoiceActionsProvider({ children }) {
  // Registry: Map<id, {phrases, handler, label, scope}>
  const [actions, setActions] = useState({});
  const idCounter = useRef(0);

  const register = useCallback((spec) => {
    const id = `va_${++idCounter.current}`;
    setActions((m) => ({ ...m, [id]: spec }));
    return id;
  }, []);

  const unregister = useCallback((id) => {
    setActions((m) => {
      const copy = { ...m };
      delete copy[id];
      return copy;
    });
  }, []);

  const dispatch = useCallback((transcript) => {
    // Score every registered action — pick the highest, gate at 65+
    let best = null;
    let bestScore = 0;
    for (const [id, spec] of Object.entries(actions)) {
      const s = _score(transcript, spec.phrases || []);
      if (s > bestScore) { best = { id, ...spec }; bestScore = s; }
    }
    if (best && bestScore >= 65) {
      try {
        const args = { transcript, score: bestScore };
        const r = best.handler(args);
        return { matched: true, score: bestScore, label: best.label,
                  result: r };
      } catch (err) {
        return { matched: false, error: String(err) };
      }
    }
    return { matched: false, score: bestScore };
  }, [actions]);

  const list = useMemo(() =>
    Object.entries(actions).map(([id, a]) => ({
      id, label: a.label, phrases: a.phrases, scope: a.scope })),
  [actions]);

  return (
    <VoiceActionsContext.Provider value={{ register, unregister, dispatch, list }}>
      {children}
    </VoiceActionsContext.Provider>
  );
}

export function useVoiceActions() {
  const ctx = useContext(VoiceActionsContext);
  if (!ctx) {
    // Safe fallback for pages that render outside the provider
    return {
      register: () => null, unregister: () => null,
      dispatch: () => ({ matched: false }), list: [],
    };
  }
  return ctx;
}

/* ── useVoiceAction — page-level convenience hook ─────────────── */
/**
 * Usage:
 *   useVoiceAction({
 *     phrases: ["save", "save changes", "save my profile"],
 *     handler: () => onSave(),
 *     label:   "Save profile",
 *   });
 */
export function useVoiceAction(spec) {
  const { register, unregister } = useVoiceActions();
  const specRef = useRef(spec);
  specRef.current = spec;

  useEffect(() => {
    if (!spec || !spec.phrases || spec.phrases.length === 0) return;
    const id = register({
      ...specRef.current,
      handler: (args) => specRef.current.handler?.(args),
    });
    return () => unregister(id);
    // We intentionally register once per mount; live phrase changes via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister]);
}
